import { PROVIDER_COUNT_TARGET } from './filters/useProviderCounts'
import type {
  MrtColumnConfig,
  MrtColumnFilter,
  MrtCountEndpoint,
  MrtCountTarget,
  MrtMultiStepFilter,
} from './mrtCollectionTypes'
import { groupsWithProviderPath, withProviderPath } from './withProviderPath'

export type SharedGroupOptions = {
  // The path from the collection's own entity to the provider ('product.provider'), or '' when the row is
  // the provider itself. Every risk and assessment filter is on the provider, so each is sent through it.
  providerPath: string
  // The table's own count endpoint, so the values its facets can't count are counted on its rows. Without
  // one they are counted on all the providers.
  countEndpoint?: MrtCountEndpoint
}

/** Where the table's rows are counted, or undefined when it can't count them and all the providers are counted. */
export const countTargetOf = ({ providerPath, countEndpoint }: SharedGroupOptions): MrtCountTarget | undefined => {
  if (countEndpoint) return { ...countEndpoint, path: providerPath }
  return providerPath ? undefined : PROVIDER_COUNT_TARGET
}

// Without a count target, the values are counted on all the providers, which the table's other filters
// aren't written for, so those are left out.
const withValueCounting = (multiStep: MrtMultiStepFilter, options: SharedGroupOptions): MrtMultiStepFilter => {
  const countTarget = countTargetOf(options)
  return {
    ...multiStep,
    useValueOptions: (path, otherFilterGroups) =>
      countTarget
        ? multiStep.useValueOptions(path, otherFilterGroups, countTarget)
        : multiStep.useValueOptions(path, []),
  }
}

// The filters are written as if the row is the provider, so the table's query and its facets go through the
// path. Their value counts go through it too where the table can count its own rows.
const throughProviderPath = (options: SharedGroupOptions, filter: MrtColumnFilter): MrtColumnFilter => {
  const { providerPath } = options
  if (!providerPath && !options.countEndpoint) return filter
  return {
    ...filter,
    toFilterGroups: (value, operator) => groupsWithProviderPath(providerPath, filter.toFilterGroups(value, operator)),
    ...(filter.facet && {
      facet: {
        ...filter.facet,
        name: withProviderPath(providerPath, filter.facet.name),
        ...(filter.facet.filterGroups && {
          filterGroups: groupsWithProviderPath(providerPath, filter.facet.filterGroups),
        }),
      },
    }),
    ...(filter.multiStep && { multiStep: withValueCounting(filter.multiStep, options) }),
  }
}

/** A filter-only column for a filter on the provider, sent through the path from the page's rows. */
export const providerFilterColumn = <T>(
  options: SharedGroupOptions,
  id: string,
  headerIntlId: string,
  filter: MrtColumnFilter,
): MrtColumnConfig<T> => ({
  id,
  headerIntlId,
  includes: [],
  isFilterOnly: true,
  renderCell: () => null,
  filter: throughProviderPath(options, filter),
})
